import { CircularProgress, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import TotalAssets from "./components/totalAssets";
import TotalSales from "./components/totalSales";
import ColumnCharts from "./components/columnCharts";
import DeviceTransactionDetails from "./components/deviceTransactionDetails";
import StackKit from "@/shared/components/kit/stack";
import GetPOSDevice from "./components/getPOSDevice";
import CustomerClub from "./components/customerClub";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchListListMerchantName } from "@/api/merchant/request.get";
import CardBoxLayout from "../../layout/cardBoxLayout";
import CustomButton from "@/shared/components/kit/button";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { fetchChartMerchantName, fetchListPazirandeDashboardChart, } from "@/api/merchant/request.post";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

/**
 * HomeMerchant
 *
 * کامپوننت داشبورد اصلی پذیرنده که مجموعه‌ای از ویجت‌ها، نمودارها و اطلاعات تجمیعی
 * را بر اساس داده‌های پذیرنده یا حالت "مجموع" نمایش می‌دهد.
 *
 * این کامپوننت شامل قابلیت‌های زیر است:
 *
 * - دریافت لیست پذیرنده‌ها و اطلاعات تجمیعی از طریق React Query
 * - امکان انتخاب پذیرنده خاص یا مشاهده داده‌های مجموع
 * - فراخوانی API برای دریافت نمودارهای بازه‌های زمانی مختلف (هفتگی، ماهانه و ...)
 * - رندر کارت‌های "مجموع دارایی‌ها" و "مجموع فروش" برای حالت‌های مختلف
 * - نمایش نمودار ستونی، اطلاعات دستگاه‌های POS و کلاب مشتریان
 * - مدیریت وضعیت لودینگ، انتخاب‌ها و تب‌های بازه زمانی
 *
 * State‌ها:
 * @state selected شناسه پذیرنده انتخاب‌شده یا مقدار "all" برای نمایش مجموع
 * @state selectedTypeChart نوع بازه زمانی نمودار (weekly, monthly, ...)
 * @state activeTab عنوان بازه زمانی انتخاب‌شده برای نمایش
 * @state dataChartPazirandeh داده‌های نمودار پذیرنده
 * @state data داده کامل پذیرنده یا مجموع پذیرنده‌ها
 * @state dataPazirande داده‌های پذیرنده انتخاب‌شده پس از دریافت از API
 * @state isChartLoading وضعیت لودینگ برای تغییر تب‌های نمودار
 * @state isMerchantLoading وضعیت لودینگ برای تغییر پذیرنده
 *
 * APIها:
 * @api fetchListListMerchantName دریافت لیست پذیرنده‌ها و داده‌های مجموع
 * @api fetchListPazirandeDashboardChart دریافت نمودار بازه زمانی پذیرنده‌ها
 * @api fetchChartMerchantName دریافت دیتای پذیرنده خاص بر اساس شناسه
 *
 * قابلیت‌ها:
 * - سوییچ بین پذیرنده‌ها و حالت مجموع
 * - تجمیع داده برای نمایش در اجزای TotalAssets و TotalSales
 * - رندر داشبورد سفارشی هنگام انتخاب پذیرنده خاص
 * - رندر داشبورد کامل همراه با نمودار، دستگاه‌ها و کلاب در حالت مجموع
 *
 * @returns JSX.Element صفحه داشبورد پذیرنده شامل نمودارها، کارت‌ها و اطلاعات تکمیلی
 */
const HomeMerchant = () => {
  const [selected, setSelected] = useState<any>("all");
  const [selectedTypeChart, setSelectedTypeChart] = useState<any>("weekly");
  const [activeTab, setActiveTab] = useState<any>("هفتگی");
  const [dataChartPazirandeh, setDataChartPazirandeh] = useState(null)
  const [data, setData] = useState<any>(null);
  const [dataPazirande, setDataPazirande] = useState<any>(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [isMerchantLoading, setIsMerchantLoading] = useState(false);

  const { data: dataSumButton, isFetched, isLoading, refetch } = useQuery({
    queryKey: ["transactions-data"],
    queryFn: () => fetchListListMerchantName(),
  });

  const { mutate: submitPazirandeDashboardChart } = useMutation({
    mutationFn: fetchListPazirandeDashboardChart,
    onMutate: () => {
      setIsChartLoading(true);
    },
    onSuccess: (data: any) => {
      setDataChartPazirandeh(data?.data);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
    },
    onSettled: () => {
      setIsChartLoading(false);
    },
  });

  useEffect(() => {
    const requestBody = {
      type: selectedTypeChart
    }
    submitPazirandeDashboardChart(requestBody);
  }, [activeTab]);

  const { mutate: dataChartMerchantName } = useMutation({
    mutationFn: fetchChartMerchantName,
    onMutate: () => {
      setIsChartLoading(true);
      setIsMerchantLoading(true);
    },
    onSuccess: (data: any) => {
      setDataPazirande(data?.data);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
    onSettled: () => {
      setIsChartLoading(false);
      setIsMerchantLoading(false);
    },
  });

  useEffect(() => {
    if (selected === "all") {
      setData(dataSumButton);
      setDataPazirande(null);
    }
  }, [selected, dataSumButton]);

  const handleMerchantSelect = (merchantId: any) => {
    if (merchantId === "all") {
      setSelected("all");
      setData(dataSumButton);
      setDataPazirande(null);
    } else {
      const requestBody = {
        id: merchantId,
        type: "weekly",
      };
      dataChartMerchantName(requestBody);
      setSelected(merchantId);
    }
  };

  const getCurrentData = () => {
    if (selected === "all") {
      return dataSumButton;
    } else {
      return dataPazirande;
    }
  };

  const handleTabChange = (tab: string, type: string) => {
    setActiveTab(tab);
    setSelectedTypeChart(type);
  };

  // Combine all loading states
  const showChartLoading = isChartLoading || isMerchantLoading;

  if (isLoading) {
    return (
      <StackKit sx={{ minHeight: "500px", minWidth: "100%", background: "linear-gradient(309.62deg, rgba(26, 71, 60, 0.13) -0.33%, rgba(255, 255, 255, 0.13) 78.26%)", }} justifyContent={"center"} alignItems={"center"}>
        <CircularProgress sx={{ color: "#fff" }} size={24} />
      </StackKit>
    );
  }

  return (
    <StackKit spacing={0} gap={0} sx={{ m: 0, p: 0 }}>
      <StackKit direction="row" spacing={0.5} gap={0.5} sx={{ position: "relative", width: "100%", height: "40px", }}>
        <StackKit direction="row" spacing={0.5} gap={0.5} sx={{ position: "absolute", right: "20px", bottom: "-2px" }}>
          <CustomButton
            onClick={() => {
              handleMerchantSelect("all");
              refetch();
            }}
            disabled={isMerchantLoading}
            sx={{ 
              bgcolor: "#1E1E1E", 
              height: "37px", 
              borderTopLeftRadius: "10px", 
              borderTopRightRadius: "10px", 
              borderBottomLeftRadius: 0, 
              borderBottomRightRadius: 0, 
              borderStyle: selected === "all" ? "solid solid none solid" : "none", 
              borderWidth: selected === "all" ? "2px 2px 0 2px" : 0, 
              borderColor: selected === "all" ? "#383838" : "transparent", 
              color: selected === "all" ? "red" : "green", 
              marginLeft: "3px !important",
              opacity: isMerchantLoading ? 0.5 : 1,
              cursor: isMerchantLoading ? 'not-allowed' : 'pointer'
            }}
          >
            <TypoGraphyKit variant="body1">مجموع</TypoGraphyKit>
          </CustomButton>
          {dataSumButton?.merchants?.map((btn: any) => {
            return (
              <CustomButton
                key={btn.id}
                onClick={() => handleMerchantSelect(btn.id)}
                disabled={isMerchantLoading}
                sx={{ 
                  bgcolor: "#1E1E1E", 
                  height: "37px", 
                  borderTopLeftRadius: "10px", 
                  borderTopRightRadius: "10px", 
                  borderBottomLeftRadius: 0, 
                  borderBottomRightRadius: 0, 
                  borderStyle: selected === btn.id ? "solid solid none solid" : "none", 
                  borderWidth: selected === btn.id ? "2px 2px 0 2px" : 0, 
                  borderColor: selected === btn.id ? "#383838" : "transparent", 
                  color: selected === btn.id ? "red" : "green",
                  opacity: isMerchantLoading ? 0.5 : 1,
                  cursor: isMerchantLoading ? 'not-allowed' : 'pointer'
                }}
              >
                <TypoGraphyKit variant="body1">
                  {btn.merchant_name}
                </TypoGraphyKit>
              </CustomButton>
            );
          })}
        </StackKit>
      </StackKit>
      
      <Grid container alignItems={'center'} spacing={2} sx={{ 
        bgcolor: "#1E1E1E", 
        border: "2px solid #383838", 
        paddingRight: "0", 
        borderRadius: "10px", 
        padding: '1rem',
        opacity: isMerchantLoading ? 0.7 : 1,
        transition: 'opacity 0.3s ease'
      }}>
        <StackKit sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flexDirection: { md: 'row', sm: 'column', xs: 'column' },
          marginY:'auto' 
        }}>
          <Grid size={{ md: 9, sm: 12, xs: 12 }}>
            <TotalAssets dataAssets={getCurrentData()} />
          </Grid>
          <Grid direction={'row'} justifyContent={'center'} width={'100%'} size={{ md: 3, sm: 12, xs: 12 }}>
            <TotalSales dataAssets={getCurrentData()} />
          </Grid>
        </StackKit>
        
        <Grid sx={{margin:'0 !important'}} size={12}>
          <CardBoxLayout sx={{
            margin:'0 !important', 
            position: 'relative',
            minHeight: '300px'
          }}>
            {/* Chart loading overlay */}
            {showChartLoading && (
              <StackKit 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(30, 30, 30, 0.9)',
                  zIndex: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 'inherit',
                  flexDirection: 'column'
                }}
              >
                <CircularProgress sx={{ color: "#fff" }} size={40} />
                <TypoGraphyKit variant="body2" sx={{ color: '#fff', mt: 1 }}>
                  {isMerchantLoading ? 'در حال بارگذاری اطلاعات پذیرنده...' : 'در حال بارگذاری نمودار...'}
                </TypoGraphyKit>
              </StackKit>
            )}
            
            <ColumnCharts
              isSidebar
              chartData={getCurrentData()?.all ? getCurrentData()?.all?.chart : getCurrentData()?.pazirande?.chart}
              colors={["linear-gradient(180deg, #EB6440 0%, #853924 100%)", "#4ECDC4"]}
              activeTab={activeTab}
              setActiveTab={handleTabChange}
              setSelectedTypeChart={setSelectedTypeChart}
            />
          </CardBoxLayout>
        </Grid>
        
        <Grid size={{ md: 9, sm: 12, xs: 12 }} sx={{ 
          bgcolor: "#434343", 
          borderRadius: "24px",
          opacity: isMerchantLoading ? 0.7 : 1
        }}>
          <DeviceTransactionDetails />
        </Grid>
        
        <Grid size={{ md: 3, sm: 12, xs: 12 }}>
          <StackKit sx={{ 
            bgcolor: "#434343", 
            borderRadius: "24px", 
            width: "100%", 
            height: { md: "300px", lg: "auto" },
            opacity: isMerchantLoading ? 0.7 : 1
          }}>
            <GetPOSDevice />
          </StackKit>
          
          <StackKit sx={{ 
            bgcolor: "#434343", 
            borderRadius: "24px", 
            width: "100%", 
            minHeight: "184px", 
            marginTop: "10px",
            opacity: isMerchantLoading ? 0.7 : 1
          }}>
            <CustomerClub />
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>
  );
};

export default HomeMerchant;